import { CreateRoleModel, PermissionRoleModel } from "./role-interfaces";
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RejectedValue, ErrorResponse } from 'shared/interfaces/core-interfaces';
import { AuthHeader } from 'services/authHeader';
import { RoleSliceModel, RolePermissionPairs, RoleModel, ChangeRoleModel } from "./role-interfaces";
import { Guid } from "guid-typescript";
import { API_BASE_URL as baseUri } from 'config/env';

export const GetActivePermissionRoles = createAsyncThunk<RoleModel[], void, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/GetActivePermissionRoles',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/permissionrole/getallactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RoleModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactivePermissionRoles = createAsyncThunk<RoleModel[], void, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/GetInactivePermissionRoles',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/permissionrole/getallinactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RoleModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPermissionRole = createAsyncThunk<RoleModel, Guid, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/GetPermissionRole',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/getdata?permissionRole=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as RoleModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdatePermissionRole = createAsyncThunk<ChangeRoleModel, RoleModel, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/UpdatePermissionRole',
    async (role: RoleModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(role)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as ChangeRoleModel;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivatePermissionRole = createAsyncThunk<ChangeRoleModel, Guid, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/DeactivatePermissionRole',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/deactivate?permissionRole=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as ChangeRoleModel;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivatePermissionRole = createAsyncThunk<ChangeRoleModel, Guid, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/ReactivatePermissionRole',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/reactivate?permissionRole=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as ChangeRoleModel;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeletePermissionRole = createAsyncThunk<ChangeRoleModel, Guid, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/DeletePermissionRole',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/delete?permissionRole=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as ChangeRoleModel;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreatePermissionRole = createAsyncThunk<ChangeRoleModel, CreateRoleModel, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/CreatePermissionRole',
    async (role: CreateRoleModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(role)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ChangeRoleModel;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPermissionRoleAssignment = createAsyncThunk<PermissionRoleModel, void, { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/GetPermissionRoleAssignment',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/permissionrole/getpermissionassingments', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PermissionRoleModel;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdatePermissionRoleAssignment = createAsyncThunk<RolePermissionPairs[], RolePermissionPairs[], { rejectValue: RejectedValue }>(
    'Slice/PermissionRoleSlice/UpdatePermissionRoleAssignment',
    async (rolePermissions: RolePermissionPairs[], { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/permissionrole/updatepermissionassingments', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(rolePermissions)
        });
        if (response.status === 200) {
            const updatePermissionsResponse = await response.json() as RolePermissionPairs[]
            return updatePermissionsResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: RoleSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    message: '',
    showNotification: false,
    permissions: [],
    roles: [],
    inactiveRoles: [],
    features: [],
    permissionList: [],
    listOfActiveRoles: [],
    listOfInactiveRoles: [],
    role: null,
}

export const PermissionRoleSlice = createSlice({
    name: 'PermissionRoleSlice',
    initialState: initialValue,
    reducers: {
        resetPermissionRoleSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.message = '';
            state.showNotification = false;
            state.permissions = [];
            state.roles = [];
            state.inactiveRoles = [];
            state.features = [];
            state.permissionList = [];
            state.listOfActiveRoles = [];
            state.listOfInactiveRoles = [];
            state.role = null;
        },
        resetPermissionRole: (state) => {
            state.role = null;
            state.isModalLoading = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        //GetActivePermissionRoles
        builder.addCase(GetActivePermissionRoles.fulfilled, (state, action: PayloadAction<RoleModel[]>) => {
            state.message = '';
            state.listOfActiveRoles = action.payload;
            state.isLoading = false;
        })
            .addCase(GetActivePermissionRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActivePermissionRoles.pending, state => {
                state.isLoading = true;
            })
        //GetInactivePermissionRoles
        builder.addCase(GetInactivePermissionRoles.fulfilled, (state, action: PayloadAction<RoleModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfInactiveRoles = action.payload;
        })
            .addCase(GetInactivePermissionRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactivePermissionRoles.pending, state => {
                state.isLoading = true;
            })
        //GetPermissionRole
        builder.addCase(GetPermissionRole.fulfilled, (state, action: PayloadAction<RoleModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.role = action.payload;
        })
            .addCase(GetPermissionRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPermissionRole.pending, state => {
                state.isModalLoading = true;
            })
        //UpdatePermissionRole
        builder.addCase(UpdatePermissionRole.fulfilled, (state, action: PayloadAction<ChangeRoleModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The permission role is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdatePermissionRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdatePermissionRole.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivatePermissionRole
        builder.addCase(DeactivatePermissionRole.fulfilled, (state, action: PayloadAction<ChangeRoleModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The permission role is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivatePermissionRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivatePermissionRole.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivatePermissionRole
        builder.addCase(ReactivatePermissionRole.fulfilled, (state, action: PayloadAction<ChangeRoleModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The permission role is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivatePermissionRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivatePermissionRole.pending, state => {
                state.isModalLoading = true;
            })
        //DeletePermissionRole
        builder.addCase(DeletePermissionRole.fulfilled, (state, action: PayloadAction<ChangeRoleModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The permission role is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeletePermissionRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeletePermissionRole.pending, state => {
                state.isModalLoading = true;
            })
        //CreatePermissionRole
        builder.addCase(CreatePermissionRole.fulfilled, (state, action: PayloadAction<ChangeRoleModel>) => {
            state.message = 'The permission role is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreatePermissionRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreatePermissionRole.pending, state => {
                state.isModalLoading = true;
            })
        //GetPermissionRoleAssignment
        builder.addCase(GetPermissionRoleAssignment.fulfilled, (state, action: PayloadAction<PermissionRoleModel>) => {
            state.message = '';
            state.permissionList = action.payload.permissionList;
            state.permissions = action.payload.permissions;
            state.features = action.payload.features;
            state.roles = action.payload.roles;
            state.inactiveRoles = action.payload.inactiveRoles;
            state.isLoading = false;
        })
            .addCase(GetPermissionRoleAssignment.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetPermissionRoleAssignment.pending, state => {
                state.isLoading = true;
            })
        //UpdatePermissionRoleAssignment
        builder.addCase(UpdatePermissionRoleAssignment.fulfilled, (state, action: PayloadAction<RolePermissionPairs[]>) => {
            state.message = 'Permission role assignment has been successfully changed.';
            state.statusCode = "200";
            state.isLoading = false;
            state.showNotification = true;
        })
            .addCase(UpdatePermissionRoleAssignment.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdatePermissionRoleAssignment.pending, state => {
                state.isLoading = true;
            })
    },
});

export const { resetPermissionRole, resetPermissionRoleSlice, resetStatusCode } = PermissionRoleSlice.actions;
export default PermissionRoleSlice.reducer;